import { computed, ref } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useApi } from '@shopexperts/shared';
import type { SubmitReviewByExpertIdBody } from '@shopexperts/shared';
import {
  createDirectReviewFingerprint,
  createDirectReviewIdempotencyKey,
  getDirectReviewErrorMessage,
} from '@/app/utils/directReview';

type OrganicReviewValues = Omit<SubmitReviewByExpertIdBody, 'expert_id' | 'idempotency_key'>;

export function useSubmitOrganicReview(expertId: () => number | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  const submissionFingerprint = ref<string | null>(null);
  const idempotencyKey = ref<string | null>(null);

  const mutation = useMutation({
    mutationKey: ['client', 'submitOrganicReview'],
    mutationFn: async (values: OrganicReviewValues) => {
      const resolvedExpertId = expertId();
      if (!resolvedExpertId) throw new Error('The expert could not be resolved.');

      const fingerprint = createDirectReviewFingerprint(resolvedExpertId, values);
      if (submissionFingerprint.value !== fingerprint || !idempotencyKey.value) {
        submissionFingerprint.value = fingerprint;
        idempotencyKey.value = createDirectReviewIdempotencyKey();
      }

      return api.client.reviewsV2.submitReviewByExpertId({
        body: {
          expert_id: resolvedExpertId,
          ...values,
          review_text: values.review_text.trim(),
          idempotency_key: idempotencyKey.value,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', 'reviews'] });
    },
  });

  return {
    submitReview: mutation.mutateAsync,
    isSubmitting: computed(() => mutation.isPending.value),
    isSuccess: computed(() => mutation.isSuccess.value),
    errorMessage: computed(() => mutation.error.value ? getDirectReviewErrorMessage(mutation.error.value) : null),
    reset: mutation.reset,
  };
}
